import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import Overlay from "ol/Overlay";
import { MapBrowserEvent } from "ol";
import type { Feature } from "ol";
import type { Geometry } from "ol/geom";
import { useFieldsScreenVM } from "@screens/FieldsScreen/FieldsScreenVM";
import Spinner from "@components/Spinner";
import { useConvert, useLang, useYield } from "@src/hooks/useLang";
import { trunc } from "@src/utils/BN";
import Dropdown from "@components/MapDropdown";
import { spectralLegend } from "@screens/FieldsScreen/spectralLegend";
import { trackMetricOptions } from "@screens/FieldsScreen/fieldTracksLayer";
import { useStores } from "@stores";
import { ROUTES } from "@stores/AccountStore";
import { reportsService } from "@services/reportsService";
import { toast } from "react-toastify";
import { ADD_CUSTOM_LAYER_VALUE, buildFieldViewOptions } from "@screens/FieldsScreen/FieldViewSelect";
import {
  EvenPaneDot,
  EvenPaneLabel,
  eveningStrengthLabel,
} from "@screens/FieldsScreen/YieldEvenPanel";
import { formatCellLabel } from "@screens/FieldsScreen/fieldLayers";
import { DateLabel, NdviDateStripView, formatDate } from "@screens/FieldsScreen/NdviDateStrip";
import FieldTrackTimeline from "@screens/FieldsScreen/FieldTrackTimeline";

// ── Styled components ───────────────────────────────────────

const Root = styled.div`
  width: 100%;
  min-width: 0;
  height: 100%;
  position: relative;
  .ol-viewport {
    height: 100% !important;
  }
`;

const ControlBar = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  right: 12px;
  display: flex;
  gap: 8px;
  z-index: 2;
  flex-wrap: wrap;
`;

const ReportButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  box-sizing: border-box;
  background: #68ad18;
  border: none;
  box-shadow: 0 2px 20px rgba(78, 78, 81, 0.1);
  border-radius: 4px;
  cursor: pointer;
  transition: 0.2s;
  font-family: Nunito Sans, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  white-space: nowrap;
  &:hover:not(:disabled) {
    background: #5c9a15;
  }
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const LegendBox = styled.div`
  position: absolute;
  inset: 56px 12px auto auto;
  background: #fff;
  padding: 8px 12px;
  border-radius: 6px;
  /* Below the control bar (2): its dropdown menus open down over this box. */
  z-index: 1;
  max-width: 160px;
  /* A season's worth of worked days plus the machine list outgrows a half-width
     map, so the box scrolls rather than running off the bottom. */
  max-height: calc(100% - 120px);
  overflow-y: auto;
  box-shadow: 0 2px 10px rgba(78, 78, 81, 0.1);
`;

const LegendTitle = styled.div<{ spaced?: boolean }>`
  font-family: Nunito Sans, sans-serif;
  font-weight: 700;
  font-size: 11px;
  color: #899093;
  margin-bottom: 4px;
  ${({ spaced }) => (spaced ? "margin-top: 10px;" : "")}
`;

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 14px;
  margin-bottom: 3px;
`;

/** Machine rows double as the show/hide control, like the main panel's device
 *  list — dimmed while that machine's swaths are hidden. */
const LegendToggleRow = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  height: 16px;
  margin-bottom: 3px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  opacity: ${({ active }) => (active ? 1 : 0.4)};
`;

/** A worked day in this pane's legend; clicking it reads that day alone, by
 *  time of pass, like the main panel's day list. */
const LegendDayRow = styled(LegendToggleRow)<{ focusedDay: boolean }>`
  border-radius: 3px;
  background: ${({ focusedDay }) => (focusedDay ? "#f0f2f3" : "none")};
  &:hover {
    background: #f0f2f3;
  }
`;

const LegendHint = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 10px;
  color: #899093;
  margin-bottom: 4px;
  white-space: normal;
`;

/** The focused day's time slider, along the bottom of the pane where it gets
 *  the most width. */
const TimeBox = styled.div`
  position: absolute;
  left: 12px;
  bottom: 24px;
  width: min(380px, calc(100% - 24px));
  box-sizing: border-box;
  background: #fff;
  padding: 8px 12px;
  border-radius: 6px;
  /* Below the control bar, like the legend. */
  z-index: 1;
  box-shadow: 0 2px 10px rgba(78, 78, 81, 0.1);
`;

const LegendColor = styled.div<{ bg: string }>`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  background: ${({ bg }) => bg};
  flex-shrink: 0;
`;

const LegendLabel = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: #494c5b;
  white-space: nowrap;
`;

const NdviGradient = styled.div`
  height: 10px;
  border-radius: 4px;
  background: linear-gradient(to right, #8c6440, #c87840, #f0c850, #b4dc50, #64be3c, #32a028, #147814);
`;

const NdviLabels = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: Nunito Sans, sans-serif;
  font-size: 9px;
  font-weight: 600;
  color: #494c5b;
  margin-top: 2px;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.5);
  z-index: 3;
`;

const HexTooltip = styled.div`
  background: rgba(0, 0, 0, 0.75);
  color: #fff;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  pointer-events: none;
`;

// ── Main component ──────────────────────────────────────────

const CompareMapPanel: React.FC = () => {
  const vm = useFieldsScreenVM();
  // The user's compare pane, or the yield evening's result beside the original.
  const cvm = vm.compareVM ?? vm.yieldEvenPane;
  const mapRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<Overlay | null>(null);
  const { convertYield } = useConvert();
  const locYield = useYield();
  const lang = useLang();
  const { settingsStore, seasonStore, accountStore } = useStores();
  const [reportLoading, setReportLoading] = useState(false);

  // The report correlates the LEFT map's layer (main map = vm.selectedFieldView)
  // with the RIGHT map's layer (this compare panel = cvm.viewMode). It only makes
  // sense — and is only supported — when the two sides are DIFFERENT and exactly
  // one of them is "yield" (the outcome) while the other is a predictor we can
  // sample per hex: "productivity" or "ndvi". Anything else (border, same layer,
  // productivity-vs-ndvi with no yield) yields no button.
  const leftLayer = vm.selectedFieldView as string;
  const rightLayer = (cvm?.viewMode ?? "border") as string;
  const reportPair = (() => {
    if (!cvm) return null;
    // Correlation reports burn Sentinel Hub + LLM quota — subscription only.
    if (!accountStore.subscriptionPaid) return null;
    if (leftLayer === rightLayer) return null;
    const hasYield = leftLayer === "yield" || rightLayer === "yield";
    if (!hasYield) return null;
    const predictor = leftLayer === "yield" ? rightLayer : leftLayer;
    if (predictor !== "productivity" && predictor !== "ndvi") return null;
    // Season comes from whichever side shows yield; NDVI date from the NDVI side.
    const yieldSeasonId =
      leftLayer === "yield" ? seasonStore.currentSeasonId ?? undefined : cvm.seasonId;
    const ndviDate =
      predictor !== "ndvi"
        ? undefined
        : leftLayer === "ndvi"
        ? vm.ndviSelectedDate ?? undefined
        : cvm.ndviSelectedDate ?? undefined;
    return { layerA: leftLayer, layerB: rightLayer, predictor, yieldSeasonId, ndviDate };
  })();

  const generateReport = async () => {
    if (!cvm || !reportPair || reportLoading) return;
    if (reportPair.predictor === "ndvi" && !reportPair.ndviDate) {
      toast.info("Pick an NDVI date first");
      return;
    }
    // Open the report tab synchronously inside the click gesture so the browser
    // doesn't block it as a pop-up (window.open after an await gets blocked).
    const tab = window.open("about:blank", "_blank");
    if (tab) {
      tab.document.write(
        "<title>Generating report…</title><body style='margin:0;font-family:Nunito Sans,system-ui,sans-serif'><p style='padding:28px;color:#555;font-size:15px'>Analyzing… your statistical report will open here in a few seconds.</p></body>"
      );
    }
    setReportLoading(true);
    try {
      const report = await reportsService.createCorrelationReport({
        fieldId: cvm.fieldId,
        layerA: reportPair.layerA as any,
        layerB: reportPair.layerB as any,
        seasonId: reportPair.yieldSeasonId,
        ndviDate: reportPair.ndviDate,
        units: settingsStore.units,
      });
      const url = ROUTES.REPORTS + "/" + report.id;
      if (tab) tab.location.href = url;
      else {
        // Pop-up was blocked — fall back and tell the user where it went.
        const opened = window.open(url, "_blank");
        if (!opened)
          toast.success("Report saved — find it in the Reports tab.");
      }
    } catch (e) {
      if (tab) tab.close();
      if (e instanceof Error && !e.message) toast.error("Could not generate report");
    } finally {
      setReportLoading(false);
    }
  };

  useEffect(() => {
    if (!cvm || !mapRef.current) return;
    cvm.map.setTarget(mapRef.current);
    // Force size recalculation after mount
    setTimeout(() => cvm.map.updateSize(), 100);

    const overlay = new Overlay({
      element: tooltipRef.current!,
      positioning: "bottom-center",
      offset: [0, -8],
      stopEvent: false,
    });
    overlayRef.current = overlay;
    cvm.map.addOverlay(overlay);

    const handlePointerMove = (e: MapBrowserEvent<any>) => {
      if (!overlay) return;
      let found = false;
      // The cell/zone/swath under the pointer, mirrored onto the main map.
      let mirrored: Feature<Geometry> | null = null;
      cvm.map.forEachFeatureAtPixel(e.pixel, (feature, layer) => {
        if (!mirrored && cvm.isMirrorLayer(layer))
          mirrored = feature as Feature<Geometry>;
        if (found) return;
        const label = formatCellLabel(feature, cvm.viewMode, {
          sizeUnit: cvm.cameraStats?.meta?.size_unit,
          customUnit: cvm.activeCustomLayer?.unitLabel,
          convertYield,
          locYield,
          pcsLabel: lang.fields.cameraUnitPcs,
          stonesLabel: lang.fields.cameraCellStones,
        });
        if (label != null && tooltipRef.current) {
          tooltipRef.current.textContent = label;
          overlay.setPosition(e.coordinate);
          found = true;
        }
      });
      if (!found) overlay.setPosition(undefined);

      vm.syncCursorFromCompare(e.coordinate, mirrored);
    };

    cvm.map.on("pointermove", handlePointerMove);

    // OL has no "pointer left the map" event, and without one the ghost pointer
    // freezes on the main map wherever the cursor last was.
    const viewport = cvm.map.getViewport();
    const handlePointerLeave = () => vm.hideCursorOnMain();
    viewport.addEventListener("pointerleave", handlePointerLeave);

    return () => {
      cvm.map.un("pointermove", handlePointerMove);
      viewport.removeEventListener("pointerleave", handlePointerLeave);
      vm.hideCursorOnMain();
      cvm.map.removeOverlay(overlay);
      cvm.map.setTarget(undefined);
    };
  }, [vm, cvm, convertYield, locYield, lang]);

  if (!cvm) return null;

  // Same layer menu as the left map (buildFieldViewOptions); the right pane
  // offers its own field's custom layers.
  const viewOptions = buildFieldViewOptions(lang, {
    cameraAvailable: vm.cameraAvailable,
    customLayers: cvm.customLayers,
  });
  const cameraLegendTitle =
    cvm.viewMode === "potato_count"
      ? lang.fields.mapSelecterPotatoCount
      : cvm.viewMode === "stone_percent"
      ? lang.fields.mapSelecterStonePercent
      : lang.fields.mapSelecterPotatoSize;

  return (
    <Root ref={mapRef}>
      <HexTooltip ref={tooltipRef} />

      {/* The evening's result: a label in place of the layer controls. */}
      {cvm.eveningStrength && (
        <EvenPaneLabel>
          <EvenPaneDot />
          {`${lang.fields.yieldEvenChip} · ${eveningStrengthLabel(lang.fields, cvm.eveningStrength)}`}
        </EvenPaneLabel>
      )}

      {/* Controls */}
      {!cvm.eveningStrength && (
        <ControlBar>
          <Dropdown
            inline
            title={lang.fields.fieldViewTitle}
            options={viewOptions}
            selected={cvm.viewMode}
            onSelect={(value) => {
              if (value === ADD_CUSTOM_LAYER_VALUE) {
                // Opened from the right pane, so the new layer should land here.
                vm.openCustomLayerDialog("compare");
                return;
              }
              cvm.setViewMode(value as any);
            }}
          />

          {(cvm.viewMode === "yield" || cvm.isCameraView(cvm.viewMode)) && (
            <>
              <Dropdown
                inline
                minWidth="auto"
                options={cvm.seasons.map((s) => ({
                  value: s.id,
                  title: `${s.from.getFullYear()}-${s.to.getFullYear()}`,
                }))}
                selected={cvm.seasonId}
                onSelect={(value) => cvm.setSeasonId(value)}
              />
              <Dropdown
                inline
                minWidth="auto"
                options={[
                  { title: "Normal", value: "quantize" },
                  { title: "Contrast", value: "quantile" },
                ]}
                selected={cvm.yieldScale}
                onSelect={(value) => cvm.setYieldScale(value as any)}
              />
            </>
          )}

          {cvm.viewMode === "tracks" && (
            <>
              <Dropdown
                inline
                minWidth="auto"
                options={cvm.seasons.map((s) => ({
                  value: s.id,
                  title: `${s.from.getFullYear()}-${s.to.getFullYear()}`,
                }))}
                selected={cvm.seasonId}
                onSelect={(value) => cvm.setSeasonId(value)}
              />
              <Dropdown
                inline
                minWidth="auto"
                // Same list, same gates, same order as the main panel — this pane
                // is the same feature seen twice.
                options={trackMetricOptions(
                  cvm.trackDevices,
                  accountStore.cameraInstalled,
                  lang
                )}
                selected={cvm.trackMetric}
                onSelect={(value) => cvm.setTrackMetric(value as any)}
              />
              <Dropdown
                inline
                minWidth="auto"
                options={[
                  { title: lang.fields.tracksColorValue, value: "value" },
                  { title: lang.fields.tracksColorDevice, value: "device" },
                  // A focused day has no date sequence; Date colours by time.
                  {
                    title: cvm.trackTime.day ? lang.tracksTime.color : lang.fields.tracksColorDate,
                    value: "date",
                  },
                ]}
                selected={cvm.trackColorMode}
                onSelect={(value) => cvm.setTrackColorMode(value as any)}
              />
              {/* A season's days stream in one batch at a time; without this the
                  pane looks finished while swaths are still arriving. */}
              {cvm.tracksLoading && <Spinner size={14} />}
            </>
          )}

          {reportPair && (
            <ReportButton onClick={generateReport} disabled={reportLoading}>
              {reportLoading ? (
                <>
                  <Spinner size={14} />
                  {lang.reports.generating}
                </>
              ) : (
                <>📊 {lang.reports.generateButton}</>
              )}
            </ReportButton>
          )}
        </ControlBar>
      )}

      {/* Tracks legend: the worked days when they carry the colour, and the
          machine list — which doubles as this pane's show/hide control, the same
          as on the main panel. One box, because two absolutely-positioned ones
          would sit on top of each other. */}
      {cvm.viewMode === "tracks" &&
        (cvm.trackDeviceList.length > 0 || cvm.trackDayLegend.length > 0) && (
        <LegendBox>
          {cvm.trackDayLegend.length > 0 && (
            <>
              <LegendTitle>{lang.fields.tracksDaysTitle}</LegendTitle>
              {!cvm.trackTime.day && <LegendHint>{lang.tracksTime.hint}</LegendHint>}
              {cvm.trackDayLegend.map((item) => (
                <LegendDayRow
                  key={item.day}
                  type="button"
                  focusedDay={item.day === cvm.trackTime.day}
                  active={!cvm.trackTime.day || item.day === cvm.trackTime.day}
                  aria-pressed={item.day === cvm.trackTime.day}
                  onClick={() => cvm.focusTrackDay(item.day)}
                >
                  {cvm.trackColorMode === "date" && !cvm.trackTime.day && (
                    <LegendColor bg={item.color} />
                  )}
                  <LegendLabel>{moment(item.day).format("D MMM")}</LegendLabel>
                </LegendDayRow>
              ))}
            </>
          )}

          {cvm.trackDeviceList.length > 0 && (
            <>
              <LegendTitle spaced={cvm.trackDayLegend.length > 0}>
                {lang.fields.tracksDevicesTitle}
              </LegendTitle>
              {/* From the device list, not the drawn legend — so machine names
                  and colors are right as soon as the day summary lands. */}
              {cvm.trackDeviceList.map((item) => (
                <LegendToggleRow
                  key={item.deviceId}
                  active={cvm.selectedTrackDeviceIds.includes(item.deviceId)}
                  onClick={() => cvm.toggleTrackDevice(item.deviceId)}
                >
                  <LegendColor bg={item.color} />
                  <LegendLabel>{item.name}</LegendLabel>
                </LegendToggleRow>
              ))}
            </>
          )}
        </LegendBox>
      )}

      {cvm.viewMode === "tracks" && cvm.trackTime.day && (
        <TimeBox>
          <FieldTrackTimeline
            day={cvm.trackTime.day}
            timeline={cvm.trackTime.timeline}
            from={cvm.trackTime.from}
            to={cvm.trackTime.to}
            onWindow={cvm.setTrackTimeWindow}
            onClose={() => cvm.focusTrackDay(cvm.trackTime.day!)}
            maxTicks={6}
          />
        </TimeBox>
      )}

      {/* Custom-layer legend. Values are raw — a customer-supplied unit is
          whatever their file said, so it is never run through convertYield. */}
      {cvm.isCustomView(cvm.viewMode) && cvm.customLegend.length > 0 && (
        <LegendBox>
          <LegendTitle>
            {cvm.activeCustomLayer?.name ?? lang.fields.customLayerTitle}
            {cvm.activeCustomLayer?.unitLabel ? `, ${cvm.activeCustomLayer.unitLabel}` : ""}
          </LegendTitle>
          {cvm.customLegend.map((item, i) => (
            <LegendRow key={i}>
              <LegendColor bg={item.color} />
              <LegendLabel>
                {trunc(item.start)} - {trunc(item.end)}
              </LegendLabel>
            </LegendRow>
          ))}
        </LegendBox>
      )}

      {/* Yield legend. The evening's result shares the main map's classes, so
          each class's share is what tells the two maps apart. */}
      {cvm.viewMode === "yield" && cvm.heatmapLegend.length > 0 && (
        <LegendBox>
          <LegendTitle>Yield</LegendTitle>
          {cvm.heatmapLegend.map((item, i) => (
            <LegendRow key={i}>
              <LegendColor bg={item.color} />
              <LegendLabel>
                {trunc(convertYield(item.start))} - {trunc(convertYield(item.end))}
              </LegendLabel>
              {cvm.eveningStrength && (
                <LegendLabel style={{ marginLeft: "auto" }}>{item.percent}%</LegendLabel>
              )}
            </LegendRow>
          ))}
        </LegendBox>
      )}

      {/* Camera legend (potato count / stone % / mean size) */}
      {cvm.isCameraView(cvm.viewMode) && cvm.cameraLegend.length > 0 && (
        <LegendBox>
          <LegendTitle>{cameraLegendTitle}</LegendTitle>
          {cvm.cameraLegend.map((item, i) => (
            <LegendRow key={i}>
              <LegendColor bg={item.color} />
              <LegendLabel>
                {trunc(item.start, 1)} - {trunc(item.end, 1)}
              </LegendLabel>
            </LegendRow>
          ))}
        </LegendBox>
      )}

      {/* Spectral legend (NDVI / brightness / moisture) */}
      {cvm.isSpectralView(cvm.viewMode) && cvm.ndviSelectedDate && (() => {
        const legend = spectralLegend(cvm.spectralLayer, lang);
        return (
          <LegendBox>
            <LegendTitle>{legend.title}</LegendTitle>
            <NdviGradient style={{ background: `linear-gradient(to right, ${legend.gradient})` }} />
            <NdviLabels>
              {legend.labels.map((l, i) => (
                <span key={i}>{l}</span>
              ))}
            </NdviLabels>
            <DateLabel compact style={{ display: "block", marginTop: 4 }}>
              {formatDate(cvm.ndviSelectedDate)}
            </DateLabel>
          </LegendBox>
        );
      })()}

      {/* Spectral date strip — the main map's strip, compact */}
      {cvm.isSpectralView(cvm.viewMode) && cvm.ndviDates.length > 0 && (
        <NdviDateStripView
          key={cvm.fieldId}
          compact
          dates={cvm.ndviDates}
          selectedDate={cvm.ndviSelectedDate}
          onSelect={(date) => cvm.setNdviSelectedDate(date)}
          getThumbnail={(date) => cvm.getNdviThumbnail(date)}
          layer={cvm.spectralLayer}
          hasMore={cvm.ndviDates.length < cvm.ndviTotal}
          onLoadMore={() => cvm.loadMoreNdviDates()}
        />
      )}

      {/* Productivity legend */}
      {cvm.viewMode === "productivity" && cvm.productivityReady && cvm.productivityLegend.length > 0 && (
        <LegendBox>
          <LegendTitle>Productivity zones</LegendTitle>
          {cvm.productivityLegend.map((item) => {
            const pct = cvm.productivityStats?.zones.find((z) => z.zone === item.zone)?.percent;
            return (
              <LegendRow key={item.zone}>
                <LegendColor bg={item.color} />
                <LegendLabel>{item.label}</LegendLabel>
                {pct != null && (
                  <LegendLabel style={{ marginLeft: "auto" }}>{pct}%</LegendLabel>
                )}
              </LegendRow>
            );
          })}
        </LegendBox>
      )}

      {/* Loading */}
      {(cvm.loading || cvm.ndviLoading || cvm.productivityLoading) && (
        <LoadingOverlay>
          <Spinner />
        </LoadingOverlay>
      )}
    </Root>
  );
};

export default observer(CompareMapPanel);
